import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Alert,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import useAxios from "../../../../hooks/useAxios";
import "./QuizManagementModal.css";

const QuizManagementModal = ({ open, onClose, classId, classData, onQuizUpdate, classQuizzes = [] }) => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attachingQuiz, setAttachingQuiz] = useState(null);
  const [detachingQuiz, setDetachingQuiz] = useState(null);
  const [togglingQuiz, setTogglingQuiz] = useState(null);
  const axiosInstance = useAxios();

  useEffect(() => {
    if (open && classQuizzes.length > 0) {
      // Use the quizzes passed from the parent component
      setQuizzes(classQuizzes);
    } else if (open) {
      // Fallback: fetch quizzes if none provided
      fetchQuizzes();
    }
  }, [open, classQuizzes]);

  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      // Get all quizzes for the teacher
      const response = await axiosInstance.get("/quizzes/all");
      if (response.data.success) {
        setQuizzes(response.data.quizzes);
      } else {
        setError(response.data.message || "Failed to fetch quizzes.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleAttachQuiz = async (quizId) => {
    try {
      setAttachingQuiz(quizId);
      const response = await axiosInstance.post(`/classes/${classId}/attach-quiz/${quizId}`);
      if (response.data.success) {
        // Refresh quizzes and notify parent
        await fetchQuizzes();
        if (onQuizUpdate) onQuizUpdate();
      } else {
        setError(response.data.message || "Failed to attach quiz.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setAttachingQuiz(null);
    }
  };

  const handleDetachQuiz = async (quizId) => {
    try {
      setDetachingQuiz(quizId);
      const response = await axiosInstance.delete(`/classes/${classId}/detach-quiz/${quizId}`);
      if (response.data.success) {
        // Refresh quizzes and notify parent
        await fetchQuizzes();
        if (onQuizUpdate) onQuizUpdate();
      } else {
        setError(response.data.message || "Failed to detach quiz.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setDetachingQuiz(null);
    }
  };

  const handleToggleQuizStatus = async (quizId) => {
    try {
      setTogglingQuiz(quizId);
      const response = await axiosInstance.patch(`/classes/${classId}/toggle-quiz/${quizId}`);
      if (response.data.success) {
        // Refresh quizzes and notify parent
        await fetchQuizzes();
        if (onQuizUpdate) onQuizUpdate();
      } else {
        setError(response.data.message || "Failed to toggle quiz status.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setTogglingQuiz(null);
    }
  };

  const isQuizAttachedToClass = (quiz) => {
    // Check if the quiz is marked as attached to this class
    if (quiz.is_attached !== undefined) {
      return quiz.is_attached;
    }
    
    // Fallback: check if quiz has attached_classes (new system)
    if (quiz.attached_classes && quiz.attached_classes.length > 0) {
      return quiz.attached_classes.some(
        (classInfo) => classInfo.id === classId
      );
    }
    
    // Fallback: check if it's a legacy quiz
    if (quiz.class_id === classId) {
      return true;
    }
    
    return false;
  };

  const getQuizClassInfo = (quiz) => {
    // Check if the quiz has the new fields from the class context
    if (quiz.is_attached !== undefined) {
      return {
        id: classId,
        is_active: quiz.is_active_in_class,
        attached_at: quiz.attached_at
      };
    }
    
    // Fallback: check if quiz has attached_classes (new system)
    if (quiz.attached_classes && quiz.attached_classes.length > 0) {
      return quiz.attached_classes.find((classInfo) => classInfo.id === classId);
    }
    
    // Fallback: check if it's a legacy quiz
    if (quiz.class_id === classId) {
      return {
        id: classId,
        is_active: true,
        attached_at: quiz.created_at
      };
    }
    
    return null;
  };

  const getAttachedQuizzes = () => {
    return quizzes.filter((quiz) => isQuizAttachedToClass(quiz));
  };

  const getUnattachedQuizzes = () => {
    return quizzes.filter((quiz) => !isQuizAttachedToClass(quiz));
  };

  const renderQuizItem = (quiz, isAttached = false) => {
    const classInfo = isAttached ? getQuizClassInfo(quiz) : null;
    const isActive = classInfo ? classInfo.is_active : false;

    return (
      <ListItem key={quiz.id} className="quiz-list-item">
        <ListItemText
          primary={quiz.title}
          secondary={
            <Box>
              <Typography variant="body2" color="textSecondary">
                ID: {quiz.unique_identifier}
              </Typography>
              {isAttached && (
                <Box mt={1}>
                  <Chip
                    label={isActive ? "Active" : "Inactive"}
                    color={isActive ? "success" : "default"}
                    size="small"
                  />
                  <Typography variant="caption" display="block" mt={0.5}>
                    Attached: {new Date(classInfo.attached_at).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </Box>
          }
        />
        <ListItemSecondaryAction>
          {isAttached ? (
            <Box display="flex" alignItems="center" gap={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={() => handleToggleQuizStatus(quiz.id)}
                    disabled={togglingQuiz === quiz.id}
                    color="primary"
                  />
                }
                label={isActive ? "Active" : "Inactive"}
              />
              <IconButton
                edge="end"
                onClick={() => handleDetachQuiz(quiz.id)}
                disabled={detachingQuiz === quiz.id}
                color="error"
              >
                {detachingQuiz === quiz.id ? (
                  <CircularProgress size={20} />
                ) : (
                  <DeleteIcon />
                )}
              </IconButton>
            </Box>
          ) : (
            <IconButton
              edge="end"
              onClick={() => handleAttachQuiz(quiz.id)}
              disabled={attachingQuiz === quiz.id}
              color="primary"
            >
              {attachingQuiz === quiz.id ? (
                <CircularProgress size={20} />
              ) : (
                <AddIcon />
              )}
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  if (loading) {
    return (
      <Modal open={open} onClose={onClose}>
        <Box className="quiz-management-modal">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="quiz-management-modal">
        <Box className="modal-header">
          <Typography variant="h5" component="h2">
            Manage Quizzes for {classData?.class_name}
          </Typography>
          <Button onClick={onClose} color="inherit">
            ✕
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box className="modal-content">
          {/* Attached Quizzes Section */}
          <Typography variant="h6" gutterBottom>
            Attached Quizzes ({getAttachedQuizzes().length})
          </Typography>
          <List className="quiz-list">
            {getAttachedQuizzes().length > 0 ? (
              getAttachedQuizzes().map((quiz) => renderQuizItem(quiz, true))
            ) : (
              <ListItem>
                <ListItemText
                  primary="No quizzes attached to this class"
                  secondary="Attach quizzes from the list below to get started."
                />
              </ListItem>
            )}
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Available Quizzes Section */}
          {/* <Typography variant="h6" gutterBottom>
            Available Quizzes ({getUnattachedQuizzes().length})
          </Typography>
          <List className="quiz-list">
            {getUnattachedQuizzes().length > 0 ? (
              getUnattachedQuizzes().map((quiz) => renderQuizItem(quiz, false))
            ) : (
              <ListItem>
                <ListItemText
                  primary="No available quizzes"
                  secondary="Create new quizzes to attach them to this class."
                />
              </ListItem>
            )}
          </List> */}
        </Box>

        <Box className="modal-footer">
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default QuizManagementModal; 